import { getDomain } from "../../lib/utils/domains";
import { UUID, ISODateTimeString } from "./DatabaseTypes";
import { weAreOfferingAllFeaturesToEveryone } from "./LicensePermissions";

export type DomainSuffixRestriction = string[];

export const mergeDomainSuffixRestrictions = (toMerge: (DomainSuffixRestriction | undefined)[]): DomainSuffixRestriction | undefined => {
	const domainSuffixSet = new Set<string>();
	for (const domainSuffixListOrAllDomains of toMerge.filter( x => x != null )) {
		if (domainSuffixListOrAllDomains == undefined) {
			// If any license does not have a domain scoping restriction, then the permission is unrestricted
			// and we can return true immediately.
			return undefined;
		} else if (Array.isArray(domainSuffixListOrAllDomains)) {
			for (const domainSuffix of domainSuffixListOrAllDomains) {
				domainSuffixSet.add(domainSuffix);
			}
		}
	}
	return domainSuffixSet.size === 0 ?
		undefined :
		[...domainSuffixSet];
}

export type PageArgument = {
	pageUrl: string | undefined;
} | {
	pageDomain: string | undefined;
} | {
	pageRegisteredDomain: string | undefined;
};

export const checkPermissionOrDomainSuffixRestriction = (
	permissionOrDomainSuffixRestriction: DomainSuffixRestriction | boolean,
	pageArgument: PageArgument | undefined
): boolean => {
	if (weAreOfferingAllFeaturesToEveryone) return true;
if (typeof permissionOrDomainSuffixRestriction === "boolean") return permissionOrDomainSuffixRestriction;
if (pageArgument == null) return false;
	const { pageUrl, pageRegisteredDomain, pageDomain=getDomain(pageUrl) ?? pageRegisteredDomain } = pageArgument as { pageUrl?: string, pageDomain?: string, pageRegisteredDomain?: string };
	return pageDomain == null ? true :
		permissionOrDomainSuffixRestriction.some(
			domainSuffix => domainSuffix[0] === '.' ? (
				// The specified domain suffix starts with a dot, so we check if the page domain ends with it
				pageDomain.endsWith(domainSuffix)
			) : (
				// The specified domain suffix does not start with a dot, so we check if the page domain is equal to it
				// or if the page domain ends with a dot followed by the domain suffix
				// (prevents attacks like 'myexample.com' satisfying domainSuffix 'example.com')
				pageDomain === domainSuffix || pageDomain.endsWith(`.${domainSuffix}`)
			)
		);
}

export const DomainScopedPermissionNames = [
	"mayGenerateIpReports",
	"maySeeUnfilteredResults",
	"maySearchInboundTraffic",
	"mayGenerateCausationGraph",
	"mayGenerateDetailedEvidence",
	"mayExportData",
	"maySaveReports",
	"mayUseForCommercialPurposes",
	"maySubmitToCourt",
	"mayRunCrawler",
] as const;
export type DomainScopedPermissionName = (typeof DomainScopedPermissionNames)[number];

export const DomainScopedPermissionDescriptions = {
	mayGenerateIpReports: "IP Reports",
	maySeeUnfilteredResults: "Unfiltered Results",
	mayUseForCommercialPurposes: "Commercial Use",
	mayGenerateCausationGraph: "Causation Graphs",
	mayGenerateDetailedEvidence: "Report Details",
	maySearchInboundTraffic: "Search Inbound Traffic",
	mayExportData: "Exporting Data",
	maySaveReports: "Saving Reports",
	maySubmitToCourt: "Use in Court",
	mayRunCrawler: "Run Crawler",
} as const satisfies Record<DomainScopedPermissionName, string>;

export type LicensePermissionBooleans = Partial<Record<DomainScopedPermissionName, boolean>>
export const permissionsDeniedByLicense = <T extends LicensePermissionBooleans>(
	permissions: T
) => {
	return DomainScopedPermissionNames.filter( (permissionName) => permissions[permissionName] === false )
};

export const hasFullPermissions = <T extends LicensePermissionBooleans>(
	permissions: T
): permissions is T & Record<DomainScopedPermissionName, true> => 
	permissionsDeniedByLicense(permissions).length === 0;


export interface RequiredForFreePublicLicense extends LicensePermissionBooleans {
	readonly isFreePublicLicense: true;
}

export interface License extends LicensePermissionBooleans {
	readonly isTrial?: true;
	readonly licenseUuid: UUID;
	readonly whenIssuedIso: ISODateTimeString;
	readonly whenExpiresIso: ISODateTimeString;
	readonly validOnlyAtDomainSuffixes?: string[];
	readonly languageText?: string;
	readonly clientName: string;
	readonly jwkKeyForVerifyingThatDataWasGeneratedWithThisLicense?: JsonWebKey;
	readonly jwkKeyForSigningThatDataWasGeneratedWithThisLicense?: JsonWebKey;
}


export interface SignedLicense extends License {
	readonly signatureHex: string;
	readonly algorithm: 'ED25519';
	readonly jwkKeyForVerifyingThisLicense: JsonWebKey;
}

export interface DbSignedLicense extends SignedLicense {
	readonly whenAddedIso: ISODateTimeString;
}

export const sanitizeLicense = (licenseIncludingFieldsThatShouldNotBeSigned: License | SignedLicense | DbSignedLicense): License => {
	const {
		whenAddedIso: _whenAddedIso,
		signatureHex: _signatureHex, 
		algorithm: _algorithm,
		jwkKeyForSigningThatDataWasGeneratedWithThisLicense: _jwkKeyForSigningThatDataWasGeneratedWithThisLicense,
		jwkKeyForVerifyingThisLicense: _jwkKeyForVerifyingThisLicense,
		...licenseContainingOnlyThoseFieldsThatShouldBeSigned
	} = licenseIncludingFieldsThatShouldNotBeSigned as DbSignedLicense;
	return licenseContainingOnlyThoseFieldsThatShouldBeSigned;
}